import React, { useState } from "react";
import { PageGroupData, SwitchoverPair } from "../utils/switchoverCheck";
import { useForm } from "@mantine/form";
import dayjs from "dayjs";
import { updateSwitchover } from "../api";
import { notifications } from "@mantine/notifications";
import { ActionIcon, Box, Button, Group, ScrollArea, Select, Stack, Text, Tooltip } from "@mantine/core";
import { formatDate } from "../utils/dateFormat";
import { PencilSimpleIcon } from "@phosphor-icons/react";
import { BaseModal } from "./BaseModal";
import { DateTimePicker } from "@mantine/dates";
import { confirmDiscardChanges } from "../utils/confirm";

interface Props {
  pairs: SwitchoverPair[]
  allPages: PageGroupData[]
  onUpdate: () => void
}

interface FormValues {
  endDate: string
  nextPageGroupId: number | null
  nextStartDate: string
}

const defaultValues: FormValues = {
  endDate: '',
  nextPageGroupId: null,
  nextStartDate: '',
}

const SwitchoverSummary: React.FC<Props> = ({ pairs, allPages, onUpdate }) => {
  const [editingPair, setEditingPair] = useState<SwitchoverPair | null>(null)
  const [saving, setSaving] = useState(false)

  const form = useForm<FormValues>({
    initialValues: defaultValues,
  })

  if (pairs.length === 0) return null

  const handleOpenEdit = (pair: SwitchoverPair) => {
    setEditingPair(pair)
    form.setValues({
      endDate: dayjs(pair.switchoverDate).format('YYYY-MM-DD HH:mm:ss'),
      nextPageGroupId: pair.toPage?.id ?? null,
      nextStartDate: pair.toPage?.start_date
        ? dayjs(pair.toPage.start_date).format('YYYY-MM-DD HH:mm:ss')
        : '',
    })
    form.resetDirty()
  }

  const handleClose = () => {
    if (
      form.isDirty() &&
      !confirmDiscardChanges()
    ) {
      return
    }
    setEditingPair(null)
  }

  const handleChangeNextPageGroupId = (id: number | null) => {
    form.setFieldValue('nextPageGroupId', id)
    if (id) {
      const selectedPage = allPages.find((p) => p.id === id)
      form.setFieldValue(
        'nextStartDate',
        selectedPage?.start_date
          ? dayjs(selectedPage.start_date).format('YYYY-MM-DD HH:mm:ss')
          : '',
      )
    } else {
      form.setFieldValue('nextStartDate', '')
    }
  }

  const handleSave = async (values: FormValues) => {
    if (!editingPair || saving) return
    setSaving(true)
    try {
      await updateSwitchover(editingPair.fromPage.client_id, editingPair.fromPage.id, {
        end_date: values.endDate,
        next_page_group_id: values.nextPageGroupId ?? undefined,
        next_start_date: values.nextStartDate || undefined,
      })
      onUpdate()
      setEditingPair(null)
    } catch (e) {
      notifications.show({
        message: '保存に失敗しました。入力内容を確認してください',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  const nextPageCandidates = editingPair
    ? allPages.filter(
      (p) =>
        p.name === editingPair.fromPage.name &&
        p.enabled &&
        p.id !== editingPair.fromPage.id,
    )
    : []

  const selectedNextPage = form.values.nextPageGroupId
    ? allPages.find((p) => p.id === form.values.nextPageGroupId)
    : null

  const grouped: Record<string, SwitchoverPair[]> = {}
  for (const pair of pairs) {
    if (!grouped[pair.name]) grouped[pair.name] = []
    grouped[pair.name].push(pair)
  }

  const nameOrder = ['invite', 'invite2', 'guest']
  const sortedEntries = Object.entries(grouped).sort(
    ([a], [b]) =>
      (nameOrder.indexOf(a) === -1 ? Infinity : nameOrder.indexOf(a)) -
      (nameOrder.indexOf(b) === -1 ? Infinity : nameOrder.indexOf(b)),
  )

  return (
    <Box
      p="md"
      bg="#00000008"
      style={{ borderRadius: 8, border: '1px solid #0000001A' }}
    >
      <Text fw={600} size="sm" mb={8}>次回切り替え予定</Text>
      <Stack gap={12}>
        {sortedEntries.map(([name, namePairs]) => (
          <Box key={name} p="sm" bg="#fff" style={{ borderRadius: 4 }}>
            <Text fw={600} mb={4}>{name}</Text>
            <Stack gap={4}>
              {namePairs.map((pair) => (
                <Group key={pair.fromPage.id} gap="xs" wrap="nowrap">
                  <Text size="sm" style={{ minWidth: 60 }}>
                    ID {pair.fromPage.id} {pair.fromPage.page_description}
                  </Text>
                  <Text size="sm" c="#757575">
                    開始日時：{formatDate(pair.fromPage.start_date)}～終了日時：{formatDate(pair.switchoverDate)}
                  </Text>
                  <Text size="sm" mx={4}>→</Text>
                  {pair.toPage ? (
                    <Text size="sm" c="teal.7">
                      ID {pair.toPage.id} {pair.toPage.page_description}（開始：{formatDate(pair.toPage.start_date)}～終了：{formatDate(pair.toPage.end_date)}）
                    </Text>
                  ) : (
                    <Text size="sm" c="orange.7">（引き継ぎ先なし）</Text>
                  )}
                  <Tooltip label="編集">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => handleOpenEdit(pair)}
                    >
                      <PencilSimpleIcon size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>

      {editingPair && (
        <BaseModal
          opened={!!editingPair}
          onClose={handleClose}
          title="切り替え予定の編集"
          width={600}
          size={400}
        >
          <form
            onSubmit={form.onSubmit(handleSave)}
            style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
          >
            <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008" px={24}>
              <Stack gap="md" p="md">
                <Text fw={600}>
                  終了するページ：ID {editingPair.fromPage.id} {editingPair.fromPage.page_description}
                </Text>

                <DateTimePicker
                  label="終了日時"
                  valueFormat="YYYY-MM-DD HH:mm"
                  value={form.values.endDate || null}
                  onChange={(v) => form.setFieldValue('endDate', v ?? '')}
                  clearable
                />

                {nextPageCandidates.length === 0 ? (
                  <Box>
                    <Text size="sm" fw={500} mb={4}>引き継ぎ先ページ ID</Text>
                    <Text size="sm" c="#757575">
                      引き継ぎ先候補がありません（同じ種類で公開中のページのみ候補になります）
                    </Text>
                  </Box>
                ) : (
                  <Select
                    label="引き継ぎ先ページ ID"
                    placeholder="（なし）"
                    data={nextPageCandidates.map((p) => ({
                      value: String(p.id),
                      label: `ID ${p.id} ${p.page_description}`,
                    }))}
                    value={form.values.nextPageGroupId !== null ? String(form.values.nextPageGroupId) : null}
                    onChange={(v) => handleChangeNextPageGroupId(v ? Number(v) : null)}
                    clearable
                    searchable
                  />
                )}

                {form.values.nextPageGroupId !== null && (
                  <>
                    <DateTimePicker
                      label="開始日時"
                      valueFormat="YYYY-MM-DD HH:mm"
                      value={form.values.nextStartDate || null}
                      onChange={(v) => form.setFieldValue('nextStartDate', v ?? '')}
                      clearable
                    />
                    {selectedNextPage && (
                      <Text size="sm" c="#757575">
                        引き継ぎ先 終了日時：{formatDate(selectedNextPage.end_date)}
                      </Text>
                    )}
                  </>
                )}
              </Stack>
            </ScrollArea>

            <Box
              p="md"
              style={{
                borderTop: '1px solid #0000001A',
                backgroundColor: '#fff',
                flexShrink: 0,
              }}
            >
              <Group justify="flex-end" gap={8}>
                <Button
                  variant="outline"
                  radius="xl"
                  color="gray"
                  onClick={handleClose}
                  disabled={saving}
                >
                  キャンセル
                </Button>
                <Button
                  variant="filled"
                  radius="xl"
                  type="submit"
                  loading={saving}
                >
                  保存
                </Button>
              </Group>
            </Box>
          </form>
        </BaseModal>
      )}
    </Box>
  )
}

export default SwitchoverSummary
